import {
  useMemo,
  useState,
  type FormEvent,
} from "react";

type Props = {
  isSubmitting: boolean;
  errorMessage: string | null;
  onSubmit: (password: string) => Promise<void>;
};

type PasswordRequirement = {
  label: string;
  passed: boolean;
};

function getPasswordScore(password: string): number {
  let score = 0;

  if (password.length >= 8) {
    score += 1;
  }

  if (password.length >= 12) {
    score += 1;
  }

  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
    score += 1;
  }

  if (/\d/.test(password)) {
    score += 1;
  }

  if (/[^A-Za-z0-9]/.test(password)) {
    score += 1;
  }

  return score;
}

function getStrengthLabel(score: number): string {
  if (score <= 1) {
    return "Weak";
  }

  if (score <= 3) {
    return "Fair";
  }

  return "Strong";
}

function getStrengthBarClass(
  index: number,
  score: number,
): string {
  if (index >= score) {
    return "bg-slate-200";
  }

  if (score <= 1) {
    return "bg-red-500";
  }

  if (score <= 3) {
    return "bg-amber-500";
  }

  return "bg-emerald-500";
}

export function CreatePasswordForm({
  isSubmitting,
  errorMessage,
  onSubmit,
}: Props) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] =
    useState("");

  const [validationError, setValidationError] =
    useState<string | null>(null);

  const passwordScore = useMemo(
    () => getPasswordScore(password),
    [password],
  );

  const strengthLabel = getStrengthLabel(
    passwordScore,
  );

  const requirements = useMemo<
    PasswordRequirement[]
  >(
    () => [
      {
        label: "At least 8 characters",
        passed: password.length >= 8,
      },
      {
        label: "Uppercase and lowercase letters",
        passed:
          /[a-z]/.test(password) &&
          /[A-Z]/.test(password),
      },
      {
        label: "At least one number",
        passed: /\d/.test(password),
      },
      {
        label: "At least one special character",
        passed: /[^A-Za-z0-9]/.test(password),
      },
    ],
    [password],
  );

  const passwordsMatch =
    confirmPassword.length > 0 &&
    password === confirmPassword;

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();
    setValidationError(null);

    if (password.length < 8) {
      setValidationError(
        "Password must be at least 8 characters.",
      );
      return;
    }

    if (password !== confirmPassword) {
      setValidationError(
        "The passwords do not match.",
      );
      return;
    }

    await onSubmit(password);
  }

  return (
    <form
      className="space-y-5"
      onSubmit={handleSubmit}
    >
      <div className="space-y-2">
        <label
          htmlFor="password"
          className="text-sm font-medium text-slate-700"
        >
          Password
        </label>

        <input
          id="password"
          type="password"
          autoComplete="new-password"
          value={password}
          disabled={isSubmitting}
          onChange={(event) => {
            setPassword(event.target.value);
            setValidationError(null);
          }}
          className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:bg-slate-100"
          placeholder="Create a secure password"
          required
        />
      </div>

      {password.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-700">
              Password strength
            </p>

            <p
              className={[
                "text-sm font-semibold",
                passwordScore <= 1
                  ? "text-red-600"
                  : passwordScore <= 3
                    ? "text-amber-600"
                    : "text-emerald-600",
              ].join(" ")}
            >
              {strengthLabel}
            </p>
          </div>

          <div className="mt-3 grid grid-cols-5 gap-2">
            {[0, 1, 2, 3, 4].map((index) => (
              <div
                key={index}
                className={[
                  "h-1.5 rounded-full transition-colors",
                  getStrengthBarClass(
                    index,
                    passwordScore,
                  ),
                ].join(" ")}
              />
            ))}
          </div>

          <ul className="mt-4 space-y-2">
            {requirements.map((requirement) => (
              <li
                key={requirement.label}
                className={[
                  "flex items-center gap-2 text-xs",
                  requirement.passed
                    ? "text-emerald-700"
                    : "text-slate-500",
                ].join(" ")}
              >
                <span
                  className={[
                    "flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold",
                    requirement.passed
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-200 text-slate-500",
                  ].join(" ")}
                >
                  {requirement.passed ? "✓" : "•"}
                </span>

                {requirement.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        <label
          htmlFor="confirm-password"
          className="text-sm font-medium text-slate-700"
        >
          Confirm password
        </label>

        <input
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          disabled={isSubmitting}
          onChange={(event) => {
            setConfirmPassword(event.target.value);
            setValidationError(null);
          }}
          className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:bg-slate-100"
          placeholder="Re-enter your password"
          required
        />

        {confirmPassword.length > 0 && (
          <p
            className={[
              "text-xs font-medium",
              passwordsMatch
                ? "text-emerald-600"
                : "text-red-600",
            ].join(" ")}
          >
            {passwordsMatch
              ? "Passwords match."
              : "Passwords do not match."}
          </p>
        )}
      </div>

      {(validationError || errorMessage) && (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {validationError || errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="flex w-full items-center justify-center rounded-xl bg-emerald-600 px-4 py-3 font-semibold text-white transition hover:bg-emerald-700 focus:outline-none focus:ring-4 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:bg-emerald-300"
      >
        {isSubmitting
          ? "Activating your workspace..."
          : "Activate workspace"}
      </button>
    </form>
  );
}